<template>
    <div class="flex flex-col lg:flex-row lg:justify-between lg:items-end gap-2 lg:gap-3">
        <div class="w-full form-control">
            <label for="currentPassword" class="label">
                <span class="label-text">{{ $t('customer.editPassword.passwordLabel') }}</span>
            </label>
            <div class="join w-full">
                <input
                    id="currentPassword"
                    disabled
                    value="****************"
                    type="password"
                    class="input w-full border-none join-item"
                >
                <div class="btn btn-square shadow-none cursor-not-allowed join-item">
                    <MiscIcon name="lock" class="w-5 h-5" />
                </div>
            </div>
        </div>
        <label
            ref="editPasswordModalOpenBtn"
            tabindex="0"
            for="editPasswordModal"
            role="button"
            class="link lg:w-48 lg:py-3"
            aria-haspopup="dialog"
            :aria-expanded="showPasswordEditModal"
            @click.prevent="openModal()"
            @keydown.enter.prevent="openModal()"
        >
            {{ $t('customer.editPassword.editPasswordAction') }}
        </label>
    </div>

    <input
        id="editPasswordModal"
        v-model="showPasswordEditModal"
        type="checkbox"
        class="modal-toggle"
    >
    <div v-if="showPasswordEditModal" class="modal">
        <div class="modal-box relative w-11/12 max-w-4xl pt-4 px-6 pb-8">
            <div class="flex justify-between items-center pt-3 pb-5 border-b border-base-300 mb-6">
                <div class="text-base font-normal" role="heading" aria-level="2">
                    {{ $t('customer.editPassword.modalTitle') }}
                </div>
                <label
                    ref="editPasswordModalCloseBtn"
                    tabindex="0"
                    for="editPasswordModal"
                    class="btn btn-ghost btn-circle"
                    role="button"
                    :aria-label="$t('customer.editEmail.modalClose')"
                    @click.prevent="closeModal()"
                    @keydown.enter.prevent="closeModal()"
                >
                    <MiscIcon name="x" class="w-5 h-5" />
                    <span class="hidden">{{ $t('customer.editPassword.modalClose') }}</span>
                </label>
            </div>

            <form class="flex flex-col items-start gap-3" @submit.prevent="onSubmit()">
                <div class="form-control w-full">
                    <label for="oldPassword" class="label">
                        <span class="label-text">{{ $t('customer.editPassword.oldPasswordLabel') }}</span>
                    </label>
                    <div class="join w-full">
                        <input
                            id="oldPassword"
                            v-model="formData.password"
                            required
                            :type="showPasswordOld ? 'text' : 'password'"
                            :placeholder="$t('customer.editPassword.oldPasswordPlaceholder')"
                            class="input input-bordered w-full border-r-0 join-item"
                        >
                        <div
                            role="button"
                            tabindex="0"
                            :aria-pressed="showPasswordOld"
                            class="btn btn-square btn-outline border-border border-l-0 join-item"
                            :aria-label="showPasswordOld ? $t('customer.passwordResetForm.hideInput') : $t('customer.passwordResetForm.showInput')"
                            @click.prevent="showPasswordOld = !showPasswordOld"
                            @keydown.enter.prevent="showPasswordOld = !showPasswordOld"
                        >
                            <MiscIcon v-if="!showPasswordOld" name="eye" class="w-5 h-5" />
                            <MiscIcon v-else name="eye-off" class="w-5 h-5" />
                        </div>
                    </div>
                </div>

                <div class="form-control w-full">
                    <label for="editPassword" class="label">
                        <span class="label-text">{{ $t('customer.editPassword.newPasswordLabel') }}</span>
                    </label>
                    <div class="join w-full">
                        <input
                            id="editPassword"
                            ref="newPasswordInput"
                            v-model="formData.newPassword"
                            required
                            :type="showPassword ? 'text' : 'password'"
                            :placeholder="$t('customer.editPassword.passwordPlaceholder')"
                            :pattern="PASSWORD_PATTERN.source"
                            class="input input-bordered w-full border-r-0 join-item"
                            @input="validateNewPassword"
                        >
                        <div
                            role="button"
                            class="btn btn-square btn-outline border-border border-l-0 join-item"
                            tabindex="0"
                            :aria-pressed="showPassword"
                            :aria-label="showPassword ? $t('customer.passwordResetForm.hideInput') : $t('customer.passwordResetForm.showInput')"
                            @click.prevent="showPassword = !showPassword"
                            @keydown.enter.prevent="showPassword = !showPassword"
                        >
                            <MiscIcon v-if="!showPassword" name="eye" class="w-5 h-5" />
                            <MiscIcon v-else name="eye-off" class="w-5 h-5" />
                        </div>
                    </div>
                    <div class="label mt-1">
                        <span class="label-text-alt text-sm text-base-content-subtle font-normal leading-5">{{ $t('customer.passwordValidation.passwordInvalid') }}</span>
                    </div>
                </div>

                <div class="form-control w-full">
                    <label for="editPasswordConfirmation" class="label">
                        <span class="label-text">{{ $t('customer.editPassword.passwordConfirmationLabel') }}</span>
                    </label>
                    <div class="join w-full">
                        <input
                            id="editPasswordConfirmation"
                            ref="newPasswordConfirmInput"
                            v-model="formData.newPasswordConfirm"
                            required
                            :type="showPasswordConfirmation ? 'text' : 'password'"
                            :placeholder="$t('customer.editPassword.passwordConfirmationPlaceholder')"
                            :pattern="PASSWORD_PATTERN.source"
                            class="input input-bordered w-full border-r-0 join-item"
                            @input="validatePasswordConfirm"
                        >
                        <div
                            role="button"
                            tabindex="0"
                            :aria-pressed="showPasswordConfirmation"
                            class="btn btn-square btn-outline border-border border-l-0 join-item"
                            :aria-label="showPasswordConfirmation ? $t('customer.passwordResetForm.hideInput') : $t('customer.passwordResetForm.showInput')"
                            @click.prevent="showPasswordConfirmation = !showPasswordConfirmation"
                            @keydown.enter.prevent="showPasswordConfirmation = !showPasswordConfirmation"
                        >
                            <MiscIcon v-if="!showPasswordConfirmation" name="eye" class="w-5 h-5" />
                            <MiscIcon v-else name="eye-off" class="w-5 h-5" />
                        </div>
                    </div>
                </div>

                <button
                    type="submit"
                    :disabled="loading"
                    class="btn btn-secondary mt-9"
                    data-cy="customer-edit-pw-submit"
                >
                    <span v-if="loading" class="loading" />
                    {{ $t('customer.editPassword.modalSubmit') }}
                </button>
            </form>
        </div>
        <label
            tabindex="0"
            class="modal-backdrop cursor-pointer"
            for="editPasswordModal"
        >
            {{ $t('customer.editPassword.modalClose') }}
        </label>
    </div>
</template>

<script setup lang="ts">
import { ApiClientError } from '@shopware/api-client'

const { t } = useI18n()
const { validatePassword, PASSWORD_PATTERN } = usePasswordValidation()
const { refreshUser } = useUser()
const { updatePassword } = useCustomerPassword()

const loading = ref(false)
const showPassword = ref(false)
const showPasswordConfirmation = ref(false)
const showPasswordOld = ref(false)
const newPasswordInput = ref()
const newPasswordConfirmInput = ref()
const formData = reactive({
    password: '',
    newPassword: '',
    newPasswordConfirm: ''
})
const { showNotification } = useNotification()
const showPasswordEditModal = ref(false)
const editPasswordModalCloseBtn = ref<null | HTMLElement>(null)

function validateNewPassword () {
    validatePassword(newPasswordInput, formData.newPassword)
}

function validatePasswordConfirm () {
    validatePassword(newPasswordConfirmInput, formData.newPasswordConfirm)
}

async function onSubmit () {
    try {
        loading.value = true
        await updatePassword(formData)
        await refreshUser()
        showPasswordEditModal.value = false
        formData.password = ''
        formData.newPassword = ''
        formData.newPasswordConfirm = ''
        showNotification(t('customer.editPassword.formSuccess'), 'success')
    } catch (e: unknown) {
        if (e instanceof ApiClientError) {
            e.details?.errors.forEach(() => {
                showNotification(t('customer.editPassword.formError'), 'error', true)
            })
        } else {
            showNotification(t('customer.editPassword.formError'), 'error', true)
        }
    } finally {
        loading.value = false
    }
}

watch (() => editPasswordModalCloseBtn?.value, () => {
    editPasswordModalCloseBtn?.value?.focus()
})

function closeModal () {
    showPasswordEditModal.value = false
    editPasswordModalCloseBtn.value?.focus()
}

function openModal () {
    showPasswordEditModal.value = true
}
</script>
